import {
  EventWizard,
  EventWizardProps,
} from "@shared/modules/events/components/calendar/event-wizard/EventWizard/EventWizard.web";
import breakpoints from "@shared/theme/props/breakpoints";
import Text from "@ui-kit/typography/Text/Text.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Box from "@ui-kit/layout/Box/Box.web";
import CartTimerCheckout from "@shared/components/application/data-display/CartTimer/CartTimerCheckout.web";
import { CalendarWidgetContent } from "@app-contexts/public/features/widget/components/heatmap-calendar/NewCalendarWidget";
import { PurchaseWizardModal } from "@app/core/src/app-contexts/public/shared/checkout/components/PurchaseWizardModal";
import { useWizardContext } from "@shared/contexts/WizardContext/WizardContext";
import { useEffect, useRef, useState } from "react";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { computeABTest, ABTestResult } from "@shared/hooks/useABTest/helpers";
import { useKeyValueParams } from "@app-contexts/public/features/key-value-params/data/services/useKeyValueService";
import useMediaQuery from "@/hooks/useMediaQuery";

type AttractionEventModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
} & EventWizardProps;

// V1 Modal - Original EventWizard implementation
function AttractionEventModalV1({
  isOpen,
  onClose,
  title,
  ...rest
}: AttractionEventModalProps) {
  const [isSmallerThanXs] = useMediaQuery(`(max-width: ${breakpoints.xs}px)`);
  const { onEventSelectCallback } = rest;
  const venue = rest.venue ?? rest.event.venue;

  return (
    <Modal
      isOpen={isOpen}
      isCentered
      onClose={onClose}
      scrollBehavior="inside"
      // Size only dictates the width, not the height.
      size={isSmallerThanXs ? "full" : "6xl"}
    >
      <Modal.Header id="calendar-modal-header">
        <Text wordBreak="break-all">{title}</Text>
        <Box marginLeft="auto" paddingRight="xl">
          <CartTimerCheckout />
        </Box>
      </Modal.Header>

      <EventWizard
        onSuccess={onClose}
        onDismiss={onClose}
        promptForQuantity={rest.promptForQuantity}
        ticketTypeSelectionRequired={rest.ticketTypeSelectionRequired}
        event={rest.event}
        venue={venue}
        calendarProps={rest.calendarProps}
        onEventSelectCallback={onEventSelectCallback}
      />
    </Modal>
  );
}

// Component to handle wizard reset inside V2 modal
function CalendarWidgetContentWithReset({
  isOpen,
  ...props
}: { isOpen: boolean } & Parameters<typeof CalendarWidgetContent>[0]) {
  const wizard = useWizardContext();
  const wasOpen = useRef(false);

  useEffect(() => {
    // Reset wizard when modal opens (transitions from closed to open)
    // Only restart if wizard has steps registered
    if (isOpen && !wasOpen.current && wizard.steps.length > 0) {
      wizard.restart();
    }
    wasOpen.current = isOpen;
  }, [isOpen, wizard.steps.length, wizard]);

  return <CalendarWidgetContent {...props} />;
}

// V2 Modal - New CalendarWidgetContent implementation with wizard reset
function AttractionEventModalV2({
  isOpen,
  onClose,
  ...rest
}: AttractionEventModalProps) {
  const venue = rest.venue ?? rest.event.venue;

  return (
    <PurchaseWizardModal
      wizardId="attraction-events-v2"
      isOpen={isOpen}
      onClose={onClose}
      showOverlay
    >
      <CalendarWidgetContentWithReset
        isOpen={isOpen}
        onClose={onClose}
        promptForQuantity={rest.promptForQuantity}
        eventIds={rest.calendarProps?.eventIdArray}
        specialEventsArray={rest.calendarProps?.specialEventsArray}
        attraction={rest.event}
        venueSlug={venue.slug}
        showCheckoutSteps={false}
      />
    </PurchaseWizardModal>
  );
}

/**
 * A Modal container for the AttractionEventModalContent component
 * Uses traffic splitting strategy with Django Waffle flags and A/B testing
 */
function AttractionEventModal({
  isOpen,
  onClose,
  title,
  ...rest
}: AttractionEventModalProps) {
  const venue =
    rest.venue ??
    (typeof rest.event.venue === "number"
      ? { id: rest.event.venue }
      : rest.event.venue);

  const [version, setVersion] = useState<ABTestResult>(ABTestResult.CONTROL);

  const { isFlagActive } = useDjangoWaffle();
  const { data: keyValueParams, isLoading: isLoadingParams } =
    useKeyValueParams();

  useEffect(() => {
    const determineVersion = async () => {
      try {
        // Check Django Waffle flag
        const flagEnabled = isFlagActive(
          "sdk_use_v2_attraction_calendar_widget",
          venue?.id
        );

        // flag disabled - always go to control
        if (!flagEnabled) {
          setVersion(ABTestResult.CONTROL);
          return;
        }

        // Wait for key-value params to load
        if (isLoadingParams) {
          return;
        }

        const testResult = computeABTest(
          "new_attraction_calendar",
          keyValueParams,
          venue?.id
        );

        setVersion(testResult);
      } catch (error) {
        // Fallback to control on any error
        setVersion(ABTestResult.CONTROL);
      }
    };

    determineVersion();
  }, [venue?.id, isFlagActive, keyValueParams, isLoadingParams]);

  // Show experiment if determined by our traffic splitting logic
  if (version === ABTestResult.EXPERIMENT) {
    return (
      <AttractionEventModalV2
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        {...rest}
      />
    );
  }

  // Default to control
  return (
    <AttractionEventModalV1
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      {...rest}
    />
  );
}

export default AttractionEventModal;
